import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Search, Loader2, Star } from 'lucide-react';
import { adminSearchForms, adminPromoteToTemplate } from '../../hooks/useAdminTemplates';
import type { SearchFormResult } from '../../hooks/useAdminTemplates';

interface Props {
  onClose: () => void;
  onPromoted: () => void;
}

export const PromoteTemplateModal: React.FC<Props> = ({ onClose, onPromoted }) => {
  const [step, setStep] = useState<'search' | 'metadata'>('search');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [results, setResults] = useState<SearchFormResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [selectedForm, setSelectedForm] = useState<SearchFormResult | null>(null);

  // Metadata fields
  const [category, setCategory] = useState('');
  const [description, setDescription] = useState('');
  const [featured, setFeatured] = useState(false);
  const [promoting, setPromoting] = useState(false);

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  // Search forms
  useEffect(() => {
    if (!debouncedSearch.trim()) { setResults([]); return; }
    const doSearch = async () => {
      setSearching(true);
      const data = await adminSearchForms(debouncedSearch);
      setResults(data);
      setSearching(false);
    };
    doSearch();
  }, [debouncedSearch]);

  const handleSelect = (form: SearchFormResult) => {
    setSelectedForm(form);
    setStep('metadata');
  };

  const handlePromote = async () => {
    if (!selectedForm) return;
    setPromoting(true);
    const { error } = await adminPromoteToTemplate(selectedForm.id, category || undefined, description || undefined, featured);
    setPromoting(false);
    if (!error) {
      onPromoted();
      onClose();
    }
  };

  const modal = (
    <div className="fixed inset-0 z-50 flex items-center justify-center" onClick={onClose}>
      <div className="absolute inset-0 bg-black/50" />
      <div
        className="relative w-full max-w-lg max-h-[90dvh] overflow-y-auto bg-neutral-950 border border-white/10 rounded-2xl p-6"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-white">
            {step === 'search' ? 'Buscar Formulário' : 'Metadados do Template'}
          </h2>
          <button onClick={onClose}><X className="w-5 h-5 text-neutral-400" /></button>
        </div>

        {step === 'search' ? (
          <div>
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-500" />
              <input
                type="text"
                placeholder="Buscar por título do formulário..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                autoFocus
                className="w-full pl-10 pr-4 py-2 bg-neutral-900 border border-white/10 rounded-lg text-sm text-white placeholder-neutral-500"
              />
            </div>
            <div className="max-h-64 overflow-y-auto space-y-1">
              {searching ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-5 h-5 text-neutral-500 animate-spin" />
                </div>
              ) : results.length === 0 ? (
                <p className="text-sm text-neutral-500 text-center py-8">
                  {debouncedSearch ? 'Nenhum formulário encontrado' : 'Digite para buscar'}
                </p>
              ) : (
                results.map(form => (
                  <button
                    key={form.id}
                    onClick={() => handleSelect(form)}
                    className="w-full text-left px-3 py-2.5 rounded-lg hover:bg-white/5 transition-colors"
                  >
                    <p className="text-sm text-white">{form.title}</p>
                    <p className="text-xs text-neutral-500">
                      {form.creator_name || 'Sem nome'} · {form.fields_count} campos · {new Date(form.created_at).toLocaleDateString('pt-BR')}
                    </p>
                  </button>
                ))
              )}
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="bg-neutral-900/50 rounded-lg p-3">
              <p className="text-sm text-white">{selectedForm?.title}</p>
              <p className="text-xs text-neutral-500">{selectedForm?.fields_count} campos</p>
            </div>

            <div>
              <label className="text-xs text-neutral-400">Categoria</label>
              <input
                type="text"
                value={category}
                onChange={e => setCategory(e.target.value)}
                placeholder="Ex: feedback, lead_gen, quiz..."
                className="w-full mt-1 bg-neutral-900 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder-neutral-600"
              />
            </div>

            <div>
              <label className="text-xs text-neutral-400">Descrição</label>
              <textarea
                value={description}
                onChange={e => setDescription(e.target.value)}
                placeholder="Descrição breve do template..."
                rows={3}
                className="w-full mt-1 bg-neutral-900 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder-neutral-600 resize-none"
              />
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => setFeatured(!featured)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                  featured ? 'bg-yellow-500/10 text-yellow-400' : 'bg-neutral-900 text-neutral-500'
                }`}
              >
                <Star className={`w-3.5 h-3.5 ${featured ? 'fill-yellow-400' : ''}`} />
                Destaque
              </button>
            </div>

            <div className="flex gap-2 pt-2">
              <button
                onClick={() => setStep('search')}
                className="px-4 py-2 bg-white/5 text-neutral-400 rounded-lg text-sm"
              >
                Voltar
              </button>
              <button
                onClick={handlePromote}
                disabled={promoting}
                className="flex-1 px-4 py-2 bg-white/10 hover:bg-white/15 text-white rounded-lg text-sm font-medium disabled:opacity-50"
              >
                {promoting ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : 'Promover a Template'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(modal, document.body);
};
